import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { LibraryActivityCreate, LibraryActivityPatch } from '../types'

// Org-level Library Activities catalog (mig 035 / PR B). Long stale
// time — the catalog changes infrequently and the per-event pickers
// need it available without a round-trip on every render. Manual
// invalidation on every mutation keeps things fresh.

export function useLibraryActivities() {
  return useQuery({
    queryKey: ['library-activities'],
    queryFn: ApiClient.libraryActivities,
    staleTime: 30 * 60_000
  })
}

export function useCreateLibraryActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: LibraryActivityCreate) => ApiClient.createLibraryActivity(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['library-activities'] })
  })
}

export function usePatchLibraryActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: LibraryActivityPatch }) =>
      ApiClient.patchLibraryActivity(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['library-activities'] })
  })
}

export function useDeleteLibraryActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deleteLibraryActivity(id).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['library-activities'] })
  })
}

// Per-event copy-from-library — invalidates the event's activities
// list so the new rows appear immediately. The mutation returns the
// inserted rows + skipped count so the caller can surface a toast.
export function useCopyActivitiesFromLibrary(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (slugs: string[]) => ApiClient.copyActivitiesFromLibrary(eventId, slugs),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['activities', eventId] })
  })
}
